import { FC, useEffect, useState } from 'react';

import { Button, Col, Row, Form } from 'antd';
import { useTranslation } from 'react-i18next';

import Card from '@/components/RCard';
import { CreateReportSteps } from '@/constants';

import RDrawer from '../RDrawer';
import CustomSteps from '../RSteps';
import CreateReport from './CreateReportStep/Step3';
import SelectMetric from './SelectMetricStep/Step2';
import SelectSimulation from './SelectSimulationStep/Step1';
import StyledCol from './styles';

interface RSimReportProps {
  isOpen: boolean;
  onClose: () => void;
}

const RSimReport: FC<RSimReportProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);

  const selectedSimulations = Form.useWatch('selectedSimulations', form);
  const selectedMetric = Form.useWatch('selectedMetric', form);
  const selectedStorageType = Form.useWatch('selectedStorageType', form);
  const selectedAssets = Form.useWatch('selectedAssets', form);
  const reportName = Form.useWatch('reportName', form);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      form.resetFields();
    }
  }, [isOpen, form]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: {
        return selectedSimulations?.length > 0;
      }
      case 1: {
        return (
          selectedMetric && selectedStorageType && selectedAssets?.length > 0
        );
      }
      case 2: {
        return reportName?.trim() !== '';
      }
      default:
        return false;
    }
  };

  const steps = [
    {
      title: t('components.simReport.steps.selectSimulations'),
      content: <SelectSimulation form={form} />,
    },
    {
      title: t('components.simReport.steps.selectMetric'),
      content: <SelectMetric form={form} />,
    },
    {
      title: t('components.simReport.steps.createReport'),
      content: <CreateReport form={form} />,
    },
  ];

  return (
    <RDrawer
      title={t('components.simReport.title')}
      description={t('components.simReport.description')}
      placement="right"
      onClose={onClose}
      open={isOpen}
      destroyOnClose
      footer={false}
    >
      <StyledCol>
        <Card className="pt-16 px-16 pb-12 content-container mt-24">
          <Form form={form} layout="vertical" className="w-full">
            <Col className="steps">
              <CustomSteps current={currentStep} items={steps} type="default" />
            </Col>

            <Card className="py-24 px-24 current-card mt-24">
              {steps[currentStep].content}
            </Card>
            <Row
              className="bottom-button-group"
              align="middle"
              justify="space-between"
            >
              <Button className="btn-cancel" onClick={() => onClose()}>
                {t('components.simReport.buttons.cancel')}
              </Button>
              <div className="right-side-btn-group">
                {currentStep > CreateReportSteps.SELECT_SIMULATIONS && (
                  <Button
                    className="btn-back"
                    type="default"
                    onClick={() => handlePrev()}
                  >
                    {t('components.simReport.buttons.previous')}
                  </Button>
                )}

                {currentStep < steps.length - 1 && (
                  <Button
                    className="btn-next"
                    type="primary"
                    onClick={() => handleNext()}
                    disabled={!canProceed()}
                  >
                    {t('components.simReport.buttons.next')}
                  </Button>
                )}
                {currentStep === steps.length - 1 && (
                  <Button
                    type="primary"
                    className="btn-next"
                    onClick={() => {
                      form.validateFields().then(() => {
                        onClose();
                      });
                    }}
                  >
                    {t('components.simReport.buttons.close')}
                  </Button>
                )}
              </div>
            </Row>
          </Form>
        </Card>
      </StyledCol>
    </RDrawer>
  );
};

export default RSimReport;
